import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Switch,
  FormControlLabel,
  TextField,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
} from "@mui/material";
import { DesktopDateTimePicker } from "@mui/x-date-pickers/DesktopDateTimePicker";
import { useTranslation } from "react-i18next";
import { EditableField } from "./EditableField";
import { TemplateSelector } from "../Template/Selector";
import { AssignmentSelector } from "../Assignment/Selector";
import { AssignmentGradingSelector } from "../AssignmentGrading/Selector";
import { GroupCategorySelector } from "../GroupCategory/Selector";
import { RatingProportionSlider } from "../RatingProportion/Slider";
import {
  Campaign,
  Template,
  TemplateType,
  CampaignGradingMode,
  GroupCategory,
  CampaignState,
  CampaignType,
} from "../../types";
import dayjs, { Dayjs } from "dayjs";

interface EditableTemplateProps {
  campaign: Campaign;
  template: Template | undefined;
  templates: Template[];
  onChange: (campaign: Campaign, activityLabel: string) => void;
}

export const EditableTemplate: React.FC<EditableTemplateProps> = ({
  campaign,
  template,
  templates,
  onChange,
}) => {
  const [t] = useTranslation();
  const [selectedTemplate, setSelectedTemplate] = useState<
    Template | undefined
  >(template);
  const tPrefix =
    campaign.campaignType === CampaignType.Assignment
      ? "assignments"
      : "surveys";

  const handleSave = () => {
    if (selectedTemplate) {
      onChange(
        { ...campaign, formId: selectedTemplate.id },
        t(`${tPrefix}.events.templateChange`, {
          template: selectedTemplate.name,
        }),
      );
    }
  };

  return (
    <EditableField
      label={
        campaign.campaignType === CampaignType.Assignment
          ? t("templates.rubric")
          : t("templates.template")
      }
      value={
        template ? (
          <Link to={`/templates/${template.id}`} style={{ color: "inherit" }}>
            {template.name}
          </Link>
        ) : (
          "No template selected"
        )
      }
      editMode={
        <TemplateSelector
          selected={selectedTemplate}
          templates={templates}
          onChange={setSelectedTemplate}
          templateType={
            campaign.campaignType === CampaignType.Assignment
              ? TemplateType.Rubric
              : TemplateType.Survey
          }
        />
      }
      onSave={handleSave}
      onCancel={() => setSelectedTemplate(template)}
      canEdit={[
        CampaignState.AssignmentScheduled,
        CampaignState.Scheduled,
      ].includes(campaign.state)}
    />
  );
};

interface EditableAssignmentProps {
  campaign: Campaign;
  onChange: (campaign: Campaign, activityLabel: string) => void;
}

export const EditableAssignment = ({
  campaign,
  onChange,
}: EditableAssignmentProps) => {
  const { t } = useTranslation();
  const [enabled, setEnabled] = useState(campaign.assignmentEnabled);

  const handleSave = () => {
    onChange(
      { ...campaign, assignmentEnabled: enabled },
      t("activity.assignmentSettingsUpdated"),
    );
  };

  const handleCancel = () => {
    setEnabled(campaign.assignmentEnabled);
  };

  const canEdit = [
    CampaignState.AssignmentScheduled,
    CampaignState.AssignmentActive,
    CampaignState.AssignmentClosed,
    CampaignState.Scheduled,
  ].includes(campaign.state);

  return (
    <EditableField
      label={t("assignments.selector")}
      value={enabled ? t("assignments.yes") : t("assignments.no")}
      editMode={
        <Stack direction="row" spacing={1} alignItems="center">
          <span>{t("assignments.no")}</span>
          <Switch
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            size="small"
          />
          <span>{t("assignments.yes")}</span>
        </Stack>
      }
      onSave={handleSave}
      onCancel={handleCancel}
      canEdit={canEdit}
    />
  );
};

interface EditableGradingProps {
  campaign: Campaign;
  onChange: (campaign: Campaign, activityLabel: string) => void;
}

export const EditableGrading: React.FC<EditableGradingProps> = ({
  campaign,
  onChange,
}) => {
  const [t] = useTranslation();
  const [gradingMode, setGradingMode] = useState<
    CampaignGradingMode | undefined
  >(campaign.assignmentGradingMode);
  const tPrefix =
    campaign.campaignType === CampaignType.Assignment
      ? "assignments"
      : "surveys";

  const handleSave = () => {
    if (gradingMode) {
      onChange(
        { ...campaign, assignmentGradingMode: gradingMode },
        t(`${tPrefix}.events.assignmentGradingChange`, {
          assignmentGradingMode: gradingMode,
        }),
      );
    }
  };

  return (
    <EditableField
      label={t("assignmentsGrading.selector")}
      value={
        campaign.assignmentEnabled
          ? t(`assignmentsGrading.mode.${campaign.assignmentGradingMode}`)
          : "n/a"
      }
      editMode={
        <AssignmentGradingSelector
          selected={gradingMode}
          onChange={setGradingMode}
        />
      }
      onSave={handleSave}
      onCancel={() => setGradingMode(campaign.assignmentGradingMode)}
      canEdit={
        [
          CampaignState.AssignmentScheduled,
          CampaignState.AssignmentActive,
          CampaignState.AssignmentClosed,
          CampaignState.Scheduled,
        ].includes(campaign.state) && campaign.assignmentEnabled
      }
    />
  );
};

interface EditablePointsProps {
  campaign: Campaign;
  onUpdatePoints: (campaignId: string, points: string) => void;
}

export const EditablePoints: React.FC<EditablePointsProps> = ({
  campaign,
  onUpdatePoints,
}) => {
  const [t] = useTranslation();
  const [points, setPoints] = useState(campaign.assignmentPoints || "");

  const handleSave = () => {
    onUpdatePoints(campaign.id, points);
  };

  return (
    <EditableField
      label={t("assignments.points")}
      value={campaign.assignmentEnabled ? campaign.assignmentPoints : "n/a"}
      editMode={
        <TextField
          size="small"
          fullWidth
          value={points}
          onChange={(e) => setPoints(e.target.value)}
          type="number"
        />
      }
      onSave={handleSave}
      onCancel={() => setPoints(campaign.assignmentPoints || "")}
      canEdit={
        [
          CampaignState.AssignmentScheduled,
          CampaignState.AssignmentActive,
          CampaignState.AssignmentClosed,
          CampaignState.Scheduled,
          CampaignState.Active,
        ].includes(campaign.state) && campaign.assignmentEnabled
      }
    />
  );
};

interface EditableRatingProportionProps {
  campaign: Campaign;
  onChange: (campaign: Campaign, activityLabel: string) => void;
}

export const EditableRatingProportion: React.FC<EditableRatingProportionProps> =
  ({ campaign, onChange }) => {
    const [t] = useTranslation();
    const [proportion, setProportion] = useState(
      campaign.assignmentRatingProportion || 0,
    );
    const tPrefix =
      campaign.campaignType === CampaignType.Assignment
        ? "assignments"
        : "surveys";

    const handleSave = () => {
      onChange(
        { ...campaign, assignmentRatingProportion: proportion },
        t(`${tPrefix}.events.assignmentRatingProportionChange`, {
          assignmentRatingProportion: proportion,
        }),
      );
    };

    const displayValue =
      campaign.assignmentEnabled && campaign.assignmentGradingMode === "FA"
        ? `${campaign.assignmentRatingProportion}% based on ratings, ${
            100 - (campaign.assignmentRatingProportion || 0)
          }% based on completion`
        : "n/a";

    return (
      <EditableField
        label={t("assignments.ratingProportion")}
        value={displayValue}
        editMode={
          <RatingProportionSlider
            initialValue={proportion}
            onChangeCommitted={(value) => setProportion(value)}
          />
        }
        onSave={handleSave}
        onCancel={() => setProportion(campaign.assignmentRatingProportion || 0)}
        canEdit={
          [
            CampaignState.AssignmentScheduled,
            CampaignState.AssignmentActive,
            CampaignState.AssignmentClosed,
            CampaignState.Scheduled,
          ].includes(campaign.state) &&
          campaign.assignmentEnabled &&
          campaign.assignmentGradingMode === "FA"
        }
      />
    );
  };

interface EditableGroupCategoryProps {
  campaign: Campaign;
  categories: GroupCategory[];
  onSetGroupCategory: (
    campaign: Campaign,
    groupcategory: GroupCategory | undefined,
  ) => void;
}

export const EditableGroupCategory: React.FC<EditableGroupCategoryProps> = ({
  campaign,
  categories,
  onSetGroupCategory,
}) => {
  const [t] = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState<
    GroupCategory | undefined
  >(campaign.groupcategory);
  const tPrefix =
    campaign.campaignType === CampaignType.Assignment
      ? "assignments"
      : "surveys";

  const handleSave = () => {
    onSetGroupCategory(campaign, selectedCategory);
  };

  return (
    <EditableField
      label={t(`${tPrefix}.teamSet`)}
      value={campaign.groupcategory?.name || "No team set selected"}
      editMode={
        <GroupCategorySelector
          selected={selectedCategory}
          categories={categories}
          onChange={(groupcategory: GroupCategory | undefined) =>
            setSelectedCategory(groupcategory)
          }
        />
      }
      onSave={handleSave}
      onCancel={() => setSelectedCategory(campaign.groupcategory)}
      canEdit={[
        CampaignState.AssignmentScheduled,
        CampaignState.AssignmentActive,
        CampaignState.AssignmentClosed,
        CampaignState.Scheduled,
      ].includes(campaign.state)}
    />
  );
};

interface EditableLoopStudentProps {
  campaign: Campaign;
  onChange: (campaign: Campaign, activityLabel: string) => void;
}

export const EditableLoopStudent: React.FC<EditableLoopStudentProps> = ({
  campaign,
  onChange,
}) => {
  const [t] = useTranslation();
  const [loopStudent, setLoopStudent] = useState(campaign.loopStudent);
  const tPrefix =
    campaign.campaignType === CampaignType.Assignment
      ? "assignments"
      : "surveys";

  const handleSave = () => {
    onChange(
      { ...campaign, loopStudent },
      t(`${tPrefix}.events.loopStudentChange`, {
        enabled: loopStudent ? "enabled" : "disabled",
      }),
    );
  };

  return (
    <EditableField
      label={t("surveys.loopStudent")}
      value={campaign.loopStudent ? t("surveys.yes") : t("surveys.no")}
      editMode={
        <Stack direction="row" spacing={1} alignItems="center">
          <span>{t(`${tPrefix}.no`)}</span>
          <Switch
            checked={loopStudent}
            onChange={(e) => setLoopStudent(e.target.checked)}
            size="small"
          />
          <span>{t(`${tPrefix}.yes`)}</span>
        </Stack>
      }
      onSave={handleSave}
      onCancel={() => setLoopStudent(campaign.loopStudent)}
      canEdit={[
        CampaignState.AssignmentScheduled,
        CampaignState.AssignmentActive,
        CampaignState.AssignmentClosed,
        CampaignState.Scheduled,
      ].includes(campaign.state)}
    />
  );
};

interface EditableLoopInstructorProps {
  campaign: Campaign;
  onChange: (campaign: Campaign, activityLabel: string) => void;
}

export const EditableLoopInstructor: React.FC<EditableLoopInstructorProps> = ({
  campaign,
  onChange,
}) => {
  const [t] = useTranslation();
  const [loopInstructor, setLoopInstructor] = useState(campaign.loopInstructor);
  const tPrefix =
    campaign.campaignType === CampaignType.Assignment
      ? "assignments"
      : "surveys";

  const handleSave = () => {
    onChange(
      { ...campaign, loopInstructor },
      t(`${tPrefix}.events.loopInstructorChange`, {
        enabled: loopInstructor ? "enabled" : "disabled",
      }),
    );
  };

  return (
    <EditableField
      label={t(`${tPrefix}.loopInstructor`)}
      value={campaign.loopInstructor ? t("surveys.yes") : t("surveys.no")}
      editMode={
        <Stack direction="row" spacing={1} alignItems="center">
          <span>{t(`${tPrefix}.no`)}</span>
          <Switch
            checked={loopInstructor}
            onChange={(e) => setLoopInstructor(e.target.checked)}
            size="small"
          />
          <span>{t(`${tPrefix}.yes`)}</span>
        </Stack>
      }
      onSave={handleSave}
      onCancel={() => setLoopInstructor(campaign.loopInstructor)}
      canEdit={[
        CampaignState.AssignmentScheduled,
        CampaignState.AssignmentActive,
        CampaignState.AssignmentClosed,
        CampaignState.Scheduled,
      ].includes(campaign.state)}
    />
  );
};

// Assignment field components
interface EditableAssignmentDescriptionProps {
  campaign: Campaign;
  onUpdateAssignment: (
    assignmentId: string,
    data: { description?: string },
  ) => void;
}

export const EditableAssignmentDescription: React.FC<
  EditableAssignmentDescriptionProps
> = ({ campaign, onUpdateAssignment }) => {
  const [t] = useTranslation();
  const [description, setDescription] = useState(
    campaign.assignment?.description || "",
  );

  const handleSave = () => {
    if (campaign.assignment) {
      onUpdateAssignment(campaign.assignment.id, { description });
    }
  };

  const canEdit = [
    CampaignState.AssignmentScheduled,
    CampaignState.AssignmentActive,
    CampaignState.AssignmentClosed,
    CampaignState.Scheduled,
  ].includes(campaign.state);

  return (
    <EditableField
      label="Description"
      value={campaign.assignment?.description || "No description provided"}
      editMode={
        <TextField
          multiline
          rows={4}
          fullWidth
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Provide a detailed description of the assignment"
        />
      }
      onSave={handleSave}
      onCancel={() => setDescription(campaign.assignment?.description || "")}
      canEdit={canEdit}
    />
  );
};

interface EditableAssignmentDateProps {
  campaign: Campaign;
  onUpdateAssignment: (
    assignmentId: string,
    data: { [key: string]: string | null },
  ) => void;
  dateField: "assignmentSendDate" | "assignmentDueDate" | "assignmentCloseDate";
  label: string;
}

export const EditableAssignmentDate: React.FC<EditableAssignmentDateProps> = ({
  campaign,
  onUpdateAssignment,
  dateField,
  label,
}) => {
  const [t] = useTranslation();
  const [date, setDate] = useState<Dayjs | null>(
    campaign.assignment?.[dateField]
      ? dayjs(campaign.assignment[dateField])
      : null,
  );

  const handleSave = () => {
    if (campaign.assignment) {
      onUpdateAssignment(campaign.assignment.id, {
        [dateField]: date?.toISOString() || null,
      });
    }
  };

  const formatDisplayDate = (dateString: string | null) => {
    if (!dateString) return "Not set";
    return dayjs(dateString).format("M/DD/YYYY h:mm A");
  };

  const canEdit = [
    CampaignState.Draft,
    CampaignState.AssignmentScheduled,
    CampaignState.AssignmentActive,
    CampaignState.AssignmentClosed,
    CampaignState.Scheduled,
  ].includes(campaign.state);

  return (
    <EditableField
      label={label}
      value={formatDisplayDate(campaign.assignment?.[dateField] || null)}
      editMode={
        <DesktopDateTimePicker
          label={label}
          value={date}
          onChange={(newDate) => setDate(newDate)}
          slotProps={{
            textField: {
              fullWidth: true,
              size: "small",
            },
          }}
        />
      }
      onSave={handleSave}
      onCancel={() =>
        setDate(
          campaign.assignment?.[dateField]
            ? dayjs(campaign.assignment[dateField])
            : null,
        )
      }
      canEdit={canEdit}
    />
  );
};
